<template>
  <div>
    <AlertDismiss ref="alertDismiss" class="mx-2"></AlertDismiss>

    <b-table
        :busy="isBusy"
        :fields="fields"
        :items="filteredItems"
        select-mode="single"
        selectable
        show-empty
        sticky-header="500px"
    >
      <template #empty>
        <b-card v-if="items === null" class="text-center my-2 fs-5">
          Descargando información declaraciones...
        </b-card>
        <b-card v-else-if="items.length !== 0" class="text-center my-2 fs-5">
          No hay declaraciones que coincidan con los criterios de búsqueda
        </b-card>
        <b-card v-else class="text-center my-2 fs-5">
          Aún no se ha creado ninguna declaración
        </b-card>
      </template>

      <!-- Icono de carga de tabla -->
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner class="align-middle"></b-spinner>
          <strong class="ms-2">Cargando datos de tabla...</strong>
        </div>
      </template>

      <!--boton de detalles -->
      <template #cell(details)="row">
        <b-button
            v-if="row.item.questions && row.item.questions.length > 0"
            class="mr-2"
            size="sm"
            variant="outline-secondary"
            @click="row.toggleDetails"
        >
          <div v-if="row.detailsShowing">
            <small class="d-none d-md-inline me-2">Ocultar</small>
            <b-icon font-scale="1" icon="eye-slash-fill"></b-icon>
          </div>
          <div v-else>
            <small class="d-none d-md-inline me-2">Ver</small>
            <b-icon font-scale="1" icon="eye-fill"></b-icon>
          </div>
        </b-button>
        <span v-else>
          <!-- No questions, so no details button -->
        </span>
      </template>

      <template #cell(document)="row">
        <template v-if="row.rowSelected">
          <b-button
              v-b-tooltip.hover="'Mostrar evidencia'"
              size="sm"
              type="button"
              variant="outline-primary"
              @click="openModalStatementDocument(row)"
          >
            <b-icon font-scale="1" icon="file-earmark-medical-fill"></b-icon>
          </b-button>
        </template>
        <template v-else>
          <b-icon class="icone-not-select" font-scale="1" icon="three-dots"></b-icon>
        </template>
      </template>

      <!--cuerpo de detalle-->
      <template #row-details="row">
        <div class="card">
          <div class="card-body m-0 p-3">
            <PepStatementRowDetails :statement="row.item" />
          </div>
        </div>
      </template>

    </b-table>

  </div>
</template>

<script>
import {mapActions, mapGetters} from 'vuex';
import AlertDismiss from '@/components/shared/Alert-dismiss.vue';
import PepStatementRowDetails from './PepStatementsTable/PepStatementRowDetails.vue';
import {EventNames, InitPageSize} from '@/plugins/enums/index.js';
import { formatDateTime } from '@/plugins/complianceScripts/index.js';

export default {
  name: 'StatementsTable',

  data() {
    return {
      isBusy: false,
      items: null,
      searchQuery: '',
      dataQuery: null,

      fields: [
        {key: 'name', label: 'Nombre declarante'},
        {key: 'declarantMail', label: 'Correo declarante'},
        {key: 'creationDate', label: 'Fecha de creación', formatter: formatDateTime},
        {key: 'details', label: 'Detalle', thClass: 'col-2'},
        {key: 'document', label: 'Documento', thClass: 'col-1'},
      ],
    }
  },

  async mounted() {
    this.$eventBus.$on(EventNames.PaginationFilterPepStatements, async (query) => {
      this.isBusy = true;

      const page = query.page - 1; //se resta 1 porque el indice comienza en 0
      const pageSize = query.pageSize;

      await this.getPepStatements(page, pageSize);

      this.isBusy = false;
    });

    this.$eventBus.$on(EventNames.FilterPepStatementsAfterSearch, (query) => {
      // Ensure searchQuery is always a string.
      this.searchQuery = (typeof query === 'string') ? query : '';
    });

    this.$eventBus.$on(EventNames.CreatedPepStatement, async () => {
      this.isBusy = true;

      await this.getPepStatements(0, InitPageSize.PepStatementsPageSize);

      this.isBusy = false;
    });

    this.$eventBus.$on(EventNames.ClearPepStatementsDateFilter, async () => {
      if (!this.dateQuery) {
        return
      }
      this.isBusy = true
      this.dateQuery = null;

      await this.getPepStatements(0, InitPageSize.PepStatementsPageSize);

      this.isBusy = false;
    });

    this.$eventBus.$on(EventNames.FilterPepStatementsByDate, async (query) => {
      this.isBusy = true;

      this.dateQuery = query;

      await this.getPepStatementsByDate(query);

      this.isBusy = false;
    });
  },

  components: {
    AlertDismiss,
    PepStatementRowDetails
  },

  computed: {
    ...mapGetters(["getRole", "getSession"]),
    filteredItems() {
      if (!this.items) {
        return [];
      }
      let filtered = this.items;

      if (this.searchQuery) {
        filtered = this.filterForSearch(filtered, this.searchQuery);
      }
      return filtered;
    },
  },

  methods: {
    ...mapActions([
      "getAllPepStatementsByPage",
      "getAllPepStatementsByDate",
      "countPepStatements"
    ]),

    async getPepStatementsByDate(query) {
      this.isBusy = true;
      const response = await this.getAllPepStatementsByDate(query);
      this.isBusy = false;

      if (response.isError) {
        this.$refs.alertDismiss.show('danger', response.message || 'Error al cargar declaraciones por fecha.');
        this.items = [];
        return;
      }

      this.items = await this.processStatements(response.data);
    },

    async getPepStatements(page, pageSize) {
      this.isBusy = true;
      const response = await this.getAllPepStatementsByPage({ page, pageSize });
      this.isBusy = false;

      if (response.isError) {
        this.$refs.alertDismiss.show('danger', response.message || 'Error al cargar declaraciones.');
        this.items = [];
        return;
      }

      this.items = await this.processStatements(response.data);
    },

    async processStatements(statements) {
      if (!statements || statements.length === 0) {
        return [];
      }
      const processedStatements = statements.map((statement) => {
        return {
          id: statement.id,
          name: statement.declarant?.name,
          declarantMail: statement.declarant?.mail,
          documentId: statement.document?.id,
          creationDate: statement.creationDate,
          questions: Array.isArray(statement.question) ? statement.question : [],
          route: statement.document?.route,
        };
      });
      return processedStatements;
    },

    openModalStatementDocument(row) {
      this.$eventBus.$emit(EventNames.OpenModalPepStatementDocument, row.item.route);
    },

    filterForSearch(statements, searchQuery) {
      const normalizedQuery = searchQuery.toLowerCase().trim();

      if (!statements) return [];

      return statements.filter(item =>
          Object.values(item).some(value => {
              if (value) {
                return value.toString().toLowerCase().includes(normalizedQuery)
              
              }
              return false
          }
          )
      );
    },
  },
}
</script>
